/**
 * fvBinding — die Formatversion einer Regelwerk-Seite an die Kopfzeile hängen.
 *
 * Das Regelwerk hat **zwei** Achsen, und nur eine davon ist global:
 *
 *  - `fv` — die **Formatversion** (202604, 202610, …). Dieselbe Achse, die die
 *    Kopfzeile führt. Sie folgt ihr.
 *  - `stand` — die **Ausgabe innerhalb einer FV** (das Publikationsdatum einer
 *    Fassung, siehe `TabellenPage`/`BedingungenPage`). Sie bleibt **lokal**:
 *    ein Stand existiert nur innerhalb seiner Formatversion. Global gehalten
 *    wäre er beim ersten FV-Wechsel ein toter Wert und die Auswahl
 *    unbedienbar. Diese Datei fasst ihn deshalb nicht an.
 *
 * Drei Regeln für die FV-Achse:
 *
 *  1. **Deep-Link zuerst.** Das `?fv=` der *ankommenden* URL gewinnt und setzt
 *     die Kopfzeile nach. Was die Seite später selbst in die URL schreibt,
 *     zählt nicht als Deep-Link — sonst käme die Kopfzeile nie wieder auf
 *     „Alle Stände" zurück, weil ihr die eigene Adresszeile widerspräche.
 *  2. **Die Kopfzeile gewinnt**, sobald sie einen konkreten Stand nennt, den
 *     der Doc-Index dieser Seite kennt. Der Index kommt von der Laufzeit-API
 *     und muss sich mit den Build-Daten nicht decken.
 *  3. **„Alle Stände" gibt es hier nicht.** Eine AHB-Tabelle ohne
 *     Formatversion ist kein Dokument. Dann gilt die lokale Vorgabe — und die
 *     Seite sagt sichtbar, dass sie eine einsetzen musste (`FvNote`).
 *
 * Eine Wahl im Seiten-Select schreibt die Kopfzeile fort: es ist dieselbe
 * Achse, zwei Steuerungen dürfen nicht auseinanderlaufen.
 */
import { useCallback, useRef, useState } from "react";
import { Notice } from "@conuti-das/prince-ui";
import { releaseLabel, useRelease, useSyncReleaseFromRoute } from "@/context";
import { ALL } from "@/types/pipeline";

export interface FvBinding {
  /** Die geltende Formatversion dieser Seite (nie `ALL`). */
  fv: string;
  /** Wahl im Seiten-Select — setzt auch die globale Formatversion. */
  setFv: (next: string) => void;
  /**
   * Vorbelegung, sobald der Doc-Index da ist (kein Benutzerwille). Ändert die
   * Kopfzeile bewusst NICHT — sonst legte der bloße Aufruf der Seite die
   * globale Wahl fest.
   */
  seedFv: (next: string) => void;
  /** Kommt die geltende FV aus der Kopfzeile — oder ist sie lokale Vorgabe? */
  followsHeader: boolean;
  /**
   * Der Satz, der die Abweichung erklärt — `null`, solange Kopf und Inhalt
   * dasselbe sagen. Siehe `FvNote`.
   */
  note: string | null;
}

/**
 * @param fvs         Formatversionen, die der Doc-Index dieser Seite kennt.
 * @param routeFv     `?fv=` der ankommenden URL (einmalig, Deep-Link-Vorrang).
 */
export function useFvBinding(fvs: string[], routeFv?: string | null): FvBinding {
  const { release, setRelease } = useRelease();

  // Beim ersten Rendern eingefroren: der Wert, mit dem der Besucher ankam.
  const deepLink = useRef(routeFv ?? "").current;
  useSyncReleaseFromRoute(deepLink);

  // Sobald jemand selbst wählt, ist der Deep-Link Geschichte.
  const chosen = useRef(false);
  const [local, setLocal] = useState<string>(deepLink);

  const headerFv = release !== ALL && fvs.includes(release) ? release : "";
  // Ein Deep-Link, den der globale Kontext nicht übernehmen konnte (ein Stand,
  // den nur die API kennt) — er gilt trotzdem, bis die Seite bedient wird.
  const strayLink =
    !chosen.current && deepLink && deepLink !== release && fvs.includes(deepLink)
      ? deepLink
      : "";
  const fv = strayLink || headerFv || local;

  const setFv = useCallback(
    (next: string) => {
      chosen.current = true;
      setLocal(next);
      // Kennt der globale Kontext den Stand nicht, bleibt seine Wahl stehen;
      // die Seite arbeitet dann mit ihrem lokalen Wert weiter.
      setRelease(next);
    },
    [setRelease],
  );

  const seedFv = useCallback((next: string) => {
    setLocal((cur) => cur || next);
  }, []);

  const followsHeader = fv !== "" && fv === headerFv;

  // Der Satz entsteht hier, weil hier beide Werte zusammenkommen. Zwei Gründe
  // für eine Abweichung, zwei verschiedene Sätze — ein allgemeiner wäre in
  // beiden Fällen halb falsch.
  let note: string | null = null;
  if (fv && !followsHeader) {
    note =
      release === ALL
        ? `In der Kopfzeile ist „Alle Stände“ gewählt — eine Regelwerk-Tabelle gehört` +
          ` immer zu genau einer Formatversion. Angezeigt wird ${releaseLabel(fv)}.`
        : `In der Kopfzeile ist Stand ${release} gewählt — dafür liegen hier keine` +
          ` Dokumente vor. Angezeigt wird ${releaseLabel(fv)}.`;
  }

  return { fv, setFv, seedFv, followsHeader, note };
}

/**
 * Der sichtbare Teil von Regel 3: Weicht die Seite von der Kopfzeile ab, sagt
 * sie es. Das stumm zu tun wäre die eine Stelle, an der Kopf und Inhalt
 * Verschiedenes behaupten.
 */
export function FvNote({ note }: { note: string | null }) {
  if (!note) return null;
  return (
    <div style={{ marginBottom: "var(--s-3)" }}>
      <Notice tone="info">{note}</Notice>
    </div>
  );
}
